/**
 * El motivo REAL por el que falló una edge function.
 *
 * 🚨 POR QUÉ EXISTE. Cuando una edge function responde con un código que no es 2xx, `supabase-js`
 * no lee el cuerpo: tira un `FunctionsHttpError` con el mensaje genérico **«Edge Function returned
 * a non-2xx status code»**. Eso es todo lo que ve el usuario, y no dice absolutamente nada.
 *
 * El 12-ago-2026 el founder reportó que «Actualizar agencias» daba error. El mensaje en pantalla era
 * ese texto genérico. La función SÍ estaba diciendo el motivo —«No tenés acceso a este tenant»— pero
 * viajaba en el cuerpo de un 403 que nadie abría. Encontrar la causa llevó una investigación entera;
 * con el motivo en pantalla habría sido evidente que la guarda de permisos era el problema.
 *
 * El cuerpo real viaja en `error.context`, que es un `Response`. Esto lo abre y saca el motivo.
 *
 * 🪤 Es `async` a propósito, y no se puede evitar: leer el cuerpo de un `Response` lo es. Un
 * ayudante sincrónico tendría que devolver el mensaje genérico, o sea nada.
 *
 * 🪤 Usa `clone()` porque el cuerpo de un `Response` se lee UNA sola vez. Sin eso, el segundo
 * que quisiera leerlo —un log, un reintento— recibiría un stream ya consumido.
 *
 * Esta lógica vivía suelta dentro de `AdsDashboardPage`, donde se resolvió el mismo problema hace
 * meses y se quedó ahí. Se mueve acá para que la próxima pantalla no tenga que redescubrirlo.
 *
 * ── 🚨 Y VOLVIÓ A PASAR, CON ESTE ARCHIVO YA ESCRITO (25-sep-2026) ────────────────────────────────
 *
 * El founder: «el Drop de Vital Natura Plus acaba de hacer un registro en su cuenta pero le sale
 * error: "no se puede registrar el envío. Edge function returned a non 2xx status code". ¿A qué se
 * debe? Parecería que no le carga las tarifas. Tuve que yo mismo ir a su cuenta y registrarlo».
 *
 * La respuesta a «¿a qué se debe?» era: **a que nadie abría el sobre**. `shalom-corporativo-register`
 * estaba diciendo su motivo —«Falta el nombre de la agencia destino», «Pedido no encontrado»— en el
 * cuerpo de un 400, y `registerShipmentShalom` caía a `error.message`. Y el detalle que lo vuelve
 * caro: `fetchShalomTariff` SÍ usaba este ayudante, así que la cotización era el único camino que
 * decía la verdad — y por eso el founder sospechó de las tarifas, que no eran el problema.
 *
 * 🪤 UNA HERRAMIENTA QUE EXISTE Y NO SE USA DONDE EL PROBLEMA REAPARECE ES PEOR QUE NO TENERLA: da la
 *    sensación de estar cubierto. El 25-sep se adoptó en los diez sitios de `ordersApi` (Shalom,
 *    Aliclik, Dropi y las fotos del proveedor). Si agregás una llamada a una edge function, usalo.
 *
 * 🪤 Y NO SE USA PARA ERRORES DE POSTGREST. Un `error` de `supabase.from(...)` no es una función: no
 *    trae `context` y su `message` ya es el bueno. Un reemplazo masivo por regex se los lleva puestos.
 */

export async function errorDeEdgeFunction(
  err: unknown,
  respaldo = 'No se pudo completar la operación.',
): Promise<string> {
  const ctx = (err as { context?: unknown } | null)?.context;
  if (ctx instanceof Response) {
    try {
      const cuerpo = await ctx.clone().json();
      /*
       * 🚨 EL ORDEN IMPORTA, Y ESTABA AL REVÉS (28-sep-2026). El founder, con un registro que falló:
       *    «le sale: no se pudo registrar el envío. NO AUTORIZADO. ¿A qué se debe?». A que
       *    `shalom-register` responde `{ error: 'No autorizado', message: 'Sesión expirada o token
       *    inválido. Recargue la página e intente de nuevo.', details: '<el motivo de Supabase>' }`
       *    — o sea: `error` es la ETIQUETA y `message` es la explicación. Prefiriendo `error` se
       *    mostraba la etiqueta y se tiraba lo único que decía qué hacer.
       *
       *    Es el mismo bug de ayer un nivel más adentro: ayer abrí el sobre, hoy leo el campo que
       *    sirve. Sacar el cuerpo y elegir mal el campo deja al operador igual de a ciegas.
       *
       * 🪤 `details` SE AGREGA, NO REEMPLAZA: suele traer el motivo técnico («token expired») que es
       *    lo que permite distinguir «recargá» de «esto es un bug nuestro». Sin él, dos causas muy
       *    distintas se leen igual.
       */
      const explicacion = [cuerpo?.message, cuerpo?.detail, cuerpo?.error]
        .find((x) => typeof x === 'string' && x.trim()) as string | undefined;
      const tecnico = typeof cuerpo?.details === 'string' ? cuerpo.details.trim() : '';
      if (explicacion?.trim()) {
        return tecnico && !explicacion.includes(tecnico) ? `${explicacion} (${tecnico})` : explicacion;
      }
    } catch {
      /* cuerpo no-JSON (HTML de un gateway, texto plano): cae al respaldo de abajo */
    }
    // Sin cuerpo legible, el código de estado al menos ubica: 401/403 es permisos, 5xx es la función.
    if (ctx.status) return `${respaldo} (código ${ctx.status})`;
  }
  const msg = (err as Error)?.message;
  // El mensaje genérico de supabase-js no aporta nada: se prefiere el respaldo, que al menos está
  // escrito para quien lo va a leer.
  if (msg && !/non-2xx status code/i.test(msg)) return msg;
  return respaldo;
}
